import {ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {Product} from '../../../Model/Catalog/Product';
import {Catalog} from '../../../Model/Catalog/Catalog';
import {DisplayItem} from '../../../Model/Catalog/DisplayItem';
import {TranslateService} from '@ngx-translate/core';
import {UserManagementService} from '../../../Services/userManagment.service';
import {CatalogService} from '../../../Services/CatalogManagement/catalog.service';
import {LoginService} from '../../../Services/login.service';
import {ProductService} from '../../../Services/CatalogManagement/product.service';
import {HttpClient} from '@angular/common/http';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import {RowPreparedEvent, RowRemovedEvent} from 'devextreme/ui/data_grid';
import {lastValueFrom, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {NpInputControlContext} from "../../../ui/inputControl/np-input-control-context";
import {FallbackEventEmitter} from "../../../ui/inputControl/fallback-event-emitter";
import {NameValue} from "../../../Model/Dto/NameValue";
import {FieldNames} from "../../../Model/FieldNames";

@Component({
    selector: 'np-product-display',
    templateUrl: 'product-display.component.html',
    styleUrls: ['./product-display.component.css'],
    standalone: false
})
export class ProductDisplayComponent implements OnInit {
    context = inject(NpInputControlContext);

    _model: Product = new Product();
    @Input() set model(value: Product) {
        this._model = value;
    }
    get model(): Product {
        return this._model;
    }

    @Output() onValueChanged = new FallbackEventEmitter<any>(this.context?.valueChanged);
    @Input() showDragHandle: boolean = this.context?.showDragHandle ?? false;
    @Input() dragFilter: string = this.context?.dragFilter ?? 'noDrag';
    @Input() selectedElements: string[] = this.context?.selectedElements ?? [''];
    @Input() templateView: boolean = this.context?.templateView ?? false;
    @Output() elementSelected = new FallbackEventEmitter<string>(this.context?.elementSelected);
    @Output() excelElementDropped = new FallbackEventEmitter<any>(this.context?.elementDropped);

    addVisible: boolean = false;
    productSelectBoxData: DataSource;
    selectedNewProduct: any;
    selectedSupplierPid: string | null = null;
    private currentAbort$?: Subject<void>;
    public isLoading: boolean = true;

    searchFields: NameValue[] = [];
    selectedSearchFields: string[] = ['SUPPLIER_PID'];

    get sumPurchasePrice(): number {
        return this.model.displayItems?.reduce(
            (sum, item) => sum + (item.purchasePrice || 0) * (item.displayCount || 0), 0) || 0;
    }

    get sumSalesPrice(): number {
        return this.model.displayItems?.reduce(
            (sum, item) => sum + (item.salesPrice || 0) * (item.displayCount || 0), 0) || 0;
    }

    constructor(
        public translate: TranslateService,
        public loginService: LoginService,
        public catalogService: CatalogService,
        public userManagement: UserManagementService,
        public productService: ProductService,
        private http: HttpClient,
        private cdr: ChangeDetectorRef
    ) {
        this.createDataSource(this.catalogService.selectedCatalogId);
    }

    ngOnInit(): void {
        this.buildSearchFields();
    }

    private buildSearchFields() {
        const fieldNames = new FieldNames();
        this.searchFields = [];

        this.addSearchField(fieldNames.supplierPid);
        this.addSearchField(fieldNames.internatonalPid);
        this.addSearchField(fieldNames.manufacturerPID);
        this.addSearchField(fieldNames.supplierAltPid);

        if (this.loginService.hasAddonSUPPLIERS) {
            this.addSearchField(fieldNames.pid);
            this.addSearchField(fieldNames.supplierInternationalId);
        }
        if (this.loginService.hasAddonBauvista) {
            this.addSearchField(fieldNames.supplierBauvistaArticleNumber);
        }
    }

    private addSearchField(value: string) {
        const nv = new NameValue();
        nv.name = this.translate.instant(value);
        nv.value = value;
        this.searchFields.push(nv);
    }

    createDataSource(catalogId: string) {
        this.productSelectBoxData = new DataSource({
            store: new CustomStore({
                key: 'supplierPid',
                load: async (loadOptions) => {
                    this.isLoading = true;
                    if (this.currentAbort$) {
                        this.currentAbort$.next();
                        this.currentAbort$.complete();
                    }

                    const skip = loadOptions.skip ?? 0;
                    const take = loadOptions.take ?? 10;
                    const pageIndex = Math.floor(skip / take) + 1;

                    this.currentAbort$ = new Subject<void>();
                    const abort$ = this.currentAbort$;
                    const queryParams = {
                        catalogId: catalogId,
                        customerId: this.loginService.currentCustomer.id,
                        page: pageIndex,
                        pageSize: take,
                        filter: loadOptions.searchValue,
                        searchFields: (this.selectedSearchFields ?? []).join(',')
                    };
                    const request$ = this.http.get('api/product/searchProductsDescrshortAndSupplierWithCatalogFilterId', {params: queryParams}).pipe(
                        takeUntil(abort$)
                    );

                    return await lastValueFrom(request$)
                        .then((result: any) => {
                            this.isLoading = false;
                            return {
                                data: result,
                                totalCount: result.length < take ? skip + result.length : 100
                            };
                        })
                        .catch((error) => {
                            return {data: [], totalCount: 0};
                        });
                },
                byKey: (key: string) => {
                    const params: any = {
                        catalogId,
                        customerId: this.loginService.currentCustomer.id,
                        artIdTo: key
                    };
                    return this.http.get<any>('api/product/GetDisplayItemDataBySupplierPid', {params})
                        .toPromise();
                }
            }),
            paginate: true,
            pageSize: 10
        });
    }

    showAddItem() {
        this.selectedNewProduct = null;
        this.selectedSupplierPid = null;
        this.addVisible = true;
    }

    async addDisplayItem() {
        let data = await this.productService.getDisplayItemDataBySupplierPid(
            this.selectedNewProduct.supplierPid,
            this.loginService.currentUser.customerId,
            this.catalogService.catalog.id
        );

        var item = new DisplayItem();
        item.artIdTo = this.selectedNewProduct.supplierPid;
        item.descriptionShort = data.descriptionShort;
        item.quantityMinNumber = data.quantityMinNumber;
        item.purchasePrice = data.purchasePrice;
        item.salesPrice = data.salesPrice;
        item.displayCount = 0;
        item.disposable = false;
        item.order = this.model.displayItems.length + 1;

        this.model.displayItems.push(item);
        this.updateAndReload();
        this.addVisible = false;
    }

    async onDisplayItemPrepared(event: RowPreparedEvent<DisplayItem>) {
        if (event.data?.artIdTo == null) return;
        let result = await this.productService
            .getDisplayItemDataBySupplierPid(
                event.data.artIdTo,
                this.loginService.currentUser.customerId,
                this.catalogService.catalog.id
            )
            .catch();
        event.data.descriptionShort = result.descriptionShort;
        event.data.quantityMinNumber = result.quantityMinNumber;
        event.data.purchasePrice = result.purchasePrice;
        event.data.salesPrice = result.salesPrice;
    }

    onRowRemoved(e: RowRemovedEvent<DisplayItem>) {
        this.updateAndReload();
    }

    update(event = null) {
        this.onValueChanged.emit(event);
        this.cdr.detectChanges();
    }

    updateAndReload() {
        this.onValueChanged.emit('reload');
    }

    isVirtualCatalog(): boolean {
        return this.catalogService?.catalog?.isVirtual;
    }

    readOnly(name: string, catalog: Catalog = this.catalogService.catalog): boolean {
        return this.isVirtualCatalog() || !this.userManagement.canEdit(name, catalog);
    }
}
